import React from "react";

/* ============================================================
   2High — Drop Builder Cart Context
   ------------------------------------------------------------
   A private "Build Your Drop" cart for mix-and-match purchasing.
   NOT a real ecommerce checkout — it's a curated wishlist that
   gets sent to the operator via Telegram for confirmation.

   Persistence: localStorage so a member's drop survives reloads.
   Lines: { id, product_id, product_name, tier_key, tier_label,
            unit_price, qty, img }
   ============================================================ */

const STORAGE_KEY = "2high_drop_v1";

const Ctx = React.createContext({
  lines: [],
  add: () => {},
  remove: () => {},
  updateQty: () => {},
  clear: () => {},
  subtotal: 0,
  count: 0,
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  buildType: "Custom Mix",
});

export function useDrop() {
  return React.useContext(Ctx);
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (_) {
    return [];
  }
}

function writeStored(lines) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch (_) {}
}

function lineId(productId, tierKey) {
  return `${productId}::${tierKey || ""}`;
}

/* ---------- Build-type detection ----------
   Reads the current cart and labels it with the insider language
   ("Custom Zip", "Custom QP", "Custom HP", "Custom Full Drop",
   "Custom Mix") so the public UI and the Telegram message can
   surface the right name. */
const TIER_GRAMS = {
  "3.5": 3.5,
  "7":   7,
  "14":  14,
  "ZIP": 28,
  "QP":  113,
  "HP":  224,
  "DROP": 448,
};

function inferBuildType(lines) {
  if (!lines.length) return "Custom Mix";
  // Aggregate weight (grams) across all lines
  const grams = lines.reduce((sum, l) => {
    const g = TIER_GRAMS[l.tier_key] || 0;
    return sum + g * (l.qty || 1);
  }, 0);
  if (grams >= 448 - 0.001) return "Custom Full Drop";
  if (grams >= 224 - 0.001) return "Custom HP";
  if (grams >= 113 - 0.001) return "Custom QP";
  if (grams >= 28 - 0.001)  return "Custom ZIP";
  return "Custom Mix";
}

export function DropProvider({ children }) {
  const [lines, setLines] = React.useState(() => readStored());
  const [isOpen, setIsOpen] = React.useState(false);

  // Persist to localStorage on every change
  React.useEffect(() => { writeStored(lines); }, [lines]);

  // Cross-tab sync
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setLines(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = React.useCallback((line) => {
    if (!line || !line.product_id) return;
    const id = lineId(line.product_id, line.tier_key);
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx >= 0) {
        // Increment qty
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (next[idx].qty || 1) + (line.qty || 1) };
        return next;
      }
      return [...prev, {
        id,
        product_id: line.product_id,
        product_name: line.product_name || "",
        tier_key: line.tier_key || "",
        tier_label: line.tier_label || "",
        unit_price: Number(line.unit_price) || 0,
        qty: Number(line.qty) || 1,
        img: line.img || "",
      }];
    });
    // Auto-open drawer when adding so user sees their drop building
    setIsOpen(true);
  }, []);

  const remove = React.useCallback((id) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const updateQty = React.useCallback((id, qty) => {
    const n = Math.max(0, Math.min(99, Number(qty) || 0));
    setLines((prev) => {
      if (n === 0) return prev.filter((l) => l.id !== id);
      return prev.map((l) => (l.id === id ? { ...l, qty: n } : l));
    });
  }, []);

  const clear = React.useCallback(() => setLines([]), []);

  const subtotal = React.useMemo(
    () => lines.reduce((s, l) => s + (l.unit_price || 0) * (l.qty || 1), 0),
    [lines]
  );
  const count = React.useMemo(
    () => lines.reduce((s, l) => s + (l.qty || 0), 0),
    [lines]
  );
  const buildType = React.useMemo(() => inferBuildType(lines), [lines]);

  const value = React.useMemo(() => ({
    lines, add, remove, updateQty, clear,
    subtotal, count, buildType,
    isOpen,
    open:  () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((v) => !v),
  }), [lines, add, remove, updateQty, clear, subtotal, count, buildType, isOpen]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/* Helper — format currency to match the "$25" style used throughout */
export function money(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "$0";
  if (n === 0) return "$0";
  // No decimals unless they're meaningful
  return Number(n) % 1 === 0 ? `$${Number(n).toLocaleString()}` : `$${Number(n).toFixed(2)}`;
}
